import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {motion} from 'framer-motion'
import moment from 'moment'
import {addLocale} from 'primereact/api'
import {Badge} from 'primereact/badge'
import {Button} from 'primereact/button'
import {Calendar} from 'primereact/calendar'
import {InputTextarea} from 'primereact/inputtextarea'
import {MultiSelect} from 'primereact/multiselect'
import {Sidebar} from 'primereact/sidebar'
import {Tooltip} from 'primereact/tooltip'
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {useParams} from 'react-router-dom/cjs/react-router-dom'
import {useAppConfig} from '../../context/app/AppContext'
import {getLanguageCode} from '../../i18n/LanguageUtils'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {bgLocale} from '../form-utils/CalendarConstant'
import {component, getComposedDataList} from '../form/Constant'
import FormHeader from '../form/FormHeader'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {
    bloodsTestsTypeOptions,
    examinationList,
    genderData,
    otherTestsTypeOptions,
    radiologyOptions,
} from './ExaminationConstant'
import ExaminationUploaderForm from './ExaminationUploaderForm'
import {useExaminationCRUD} from './query-keys/ExaminationQueryManager'
import './style.css'

const inputsNeedDate = [
    'mriBrain',
    'ctChestAbdomenPelvis',
    'usLiverAndRenalTract',
    'ctCoronaryArteriogram',
    'mriProstate',
    'mammogram',
    'echo',
    '24hrEcg',
    '24hrBp',
    'colonoscopy',
]

const examinationType = {
    bloodsTests: 'bloodsTests',
    radiology: 'radiology',
    otherTests: 'otherTests',
}

const ExaminationForm = forwardRef(({gender = 'unknown'}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Translate
    const {
        translateCommons,
        translateKeys,
        translateOptions,
        translateDatatable,
    } = TranslateUtils()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {timezoneId} = useAppConfig()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const data = useSignal({})
    const errors = useSignal({})
    const imageUploadRef = useRef(null)
    //#endregion

    //#region  Locale
    addLocale('bg', bgLocale)
    //#endregion

    //#region Memo
    const genderFilterDataList = useMemo(() => {
        let dataFormList = [...examinationList]

        const femaleBloodTestsOptions = ['antiMullerianHormone']
        const femaleRadiologyTestsOptions = [
            'mammogram',
            'mammogramDate',
            'transvaginalUs',
        ]

        const maleBloodTestsOptions = ['psa']
        const maleRadiologyTestsOptions = ['mriProstate', 'mriProstateDate']

        const bloodsTestsIndex = dataFormList?.findIndex(
            item => item?.key === 'bloodsTests',
        )
        const radiologyIndex = dataFormList?.findIndex(
            item => item?.key === 'radiology',
        )

        if (
            gender !== null &&
            (gender !== genderData.unknown ||
                gender !== genderData.indeterminate)
        ) {
            if (gender === genderData.male) {
                // Bloods Tests Male filter
                dataFormList[bloodsTestsIndex] = {
                    ...dataFormList[bloodsTestsIndex],
                    children: dataFormList[bloodsTestsIndex].children.filter(
                        childItem =>
                            !femaleBloodTestsOptions.includes(childItem?.key),
                    ),
                }

                // Radiology Tests Male filter
                dataFormList[radiologyIndex] = {
                    ...dataFormList[radiologyIndex],
                    children: dataFormList[radiologyIndex].children.filter(
                        childItem =>
                            !femaleRadiologyTestsOptions.includes(
                                childItem?.key,
                            ),
                    ),
                }
            } else if (gender === genderData.female) {
                // Bloods Tests Tests Female filter
                dataFormList[bloodsTestsIndex] = {
                    ...dataFormList[bloodsTestsIndex],
                    children: dataFormList[bloodsTestsIndex].children.filter(
                        childItem =>
                            !maleBloodTestsOptions.includes(childItem?.key),
                    ),
                }
                // Radiology Tests Female filter
                dataFormList[radiologyIndex] = {
                    ...dataFormList[radiologyIndex],
                    children: dataFormList[radiologyIndex].children.filter(
                        childItem =>
                            !maleRadiologyTestsOptions.includes(childItem?.key),
                    ),
                }
            }
        }
        return dataFormList
    }, [gender])

    const dataList = useMemo(() => {
        return getComposedDataList(genderFilterDataList, data.value)
    }, [data.value, genderFilterDataList])
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const {mutateAsync: createOrUpdateExamination} = useExaminationCRUD(
        !!data.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )
    //#endregion

    //#region Handle Function
    const handleShowForm = value => {
        blockOverflownScroll()
        data.value = value || {}
        visible.value = true
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        data.value = {}
        errors.value = {}
        visible.value = false
    }

    const handleOnSubmit = async () => {
        if (!!data.value) {
            let radiologiesNeedDate = [
                'mriBrain',
                'ctChestAbdomenPelvis',
                'usLiverAndRenalTract',
                'ctCoronaryArteriogram',
                'mriProstate',
                'mammogram',
            ]

            let otherTestsNeedDate = [
                'echo',
                '24hrEcg',
                '24hrBp',
                'colonoscopy',
            ]

            // Check bloodTests
            let bloodsTestsErrors = []
            let isBloodTestsEmpty = false
            if (
                !!data.value?.bloodsTestsType &&
                data.value.bloodsTestsType?.length > 0
            ) {
                if (
                    !!data.value?.bloodsTests &&
                    !!Object.keys(data.value?.bloodsTests) &&
                    Object.keys(data.value?.bloodsTests)?.length > 0
                ) {
                    data.value?.bloodsTestsType?.forEach(key => {
                        if (
                            !data.value.bloodsTests[key]?.content ||
                            data.value.bloodsTests[key]?.content.length < 1
                        ) {
                            bloodsTestsErrors.push(key)
                        }
                    })
                } else {
                    bloodsTestsErrors.push(...data.value?.bloodsTestsType)
                }
            } else {
                if (
                    !data.value?.bloodsTests ||
                    !Object.keys(data.value?.bloodsTests) ||
                    Object.keys(data.value?.bloodsTests)?.length < 1 ||
                    (!Object.keys(data.value?.bloodsTests).includes('psa') &&
                        !Object.keys(data.value?.bloodsTests).includes(
                            'antiMullerianHormone',
                        ))
                ) {
                    isBloodTestsEmpty = true
                }
            }

            if (bloodsTestsErrors?.length > 0) {
                errors.value = {bloodsTestsErrors: [...bloodsTestsErrors]}

                showNotification(
                    severityNotification.error,
                    `${translateCommons(
                        'Please enter your examination information for bloods tests',
                    )}`,
                )

                return
            }

            // Check radiology
            let radiologyErrors = []
            let isRadiologyEmpty = false
            if (
                !!data.value?.radiologyType &&
                data.value.radiologyType?.length > 0
            ) {
                if (
                    !!data.value?.radiology &&
                    !!Object.keys(data.value?.radiology) &&
                    Object.keys(data.value?.radiology)?.length > 0
                ) {
                    data.value?.radiologyType.forEach(key => {
                        if (
                            (!!data.value?.radiology[key].content &&
                                data.value?.radiology[key]?.content?.length >
                                    0) ||
                            (!!data.value?.radiology[key].images &&
                                data.value?.radiology[key]?.images?.length > 0)
                        ) {
                            if (radiologiesNeedDate.includes(key)) {
                                if (!data.value?.radiology[key]?.date) {
                                    showNotification(
                                        severityNotification.error,
                                        `${translateCommons(
                                            'Please enter your examination time for ',
                                        )} ${translateKeys(
                                            radiologyOptions.find(
                                                item => item?.value === key,
                                            ).label,
                                        )}`,
                                    )

                                    radiologyErrors.push(key + 'Date')
                                    return
                                }
                            }
                        } else {
                            radiologyErrors.push(key)
                        }
                    })
                } else {
                    radiologyErrors.push(...data.value?.radiologyType)
                }
            } else {
                if (
                    !!data.value?.radiology &&
                    !!Object.keys(data.value?.radiology) &&
                    Object.keys(data.value?.radiology)?.length > 0
                ) {
                    if (
                        (!!data.value?.radiology['mammogram']?.content &&
                            data.value?.radiology['mammogram']?.content.length >
                                0) ||
                        (!!data.value?.otherTests['mammogram']?.images &&
                            data.value?.radiology['mammogram']?.images.length >
                                0)
                    ) {
                        if (!data.value?.radiology['mammogram']?.date) {
                            showNotification(
                                severityNotification.error,
                                `${translateCommons(
                                    'Please enter your examination time for Mammogram for Female Date',
                                )} `,
                            )

                            radiologyErrors.push('mammogramDate')
                        }
                    }
                } else {
                    isRadiologyEmpty = true
                }
            }

            if (radiologyErrors?.length > 0) {
                errors.value = {radiologyErrors: [...radiologyErrors]}

                showNotification(
                    severityNotification.error,
                    `${translateCommons(
                        'Please enter your examination information for radiology',
                    )}`,
                )
                return
            }

            // Check other tests
            let otherTestsErrors = []
            let isOtherTestsEmpty = false
            if (
                !!data.value?.otherTestsType &&
                data.value.otherTestsType?.length > 0
            ) {
                if (
                    !!data.value?.otherTests &&
                    !!Object.keys(data.value?.otherTests) &&
                    Object.keys(data.value?.otherTests)?.length > 0
                ) {
                    data.value?.otherTestsType.forEach(key => {
                        if (
                            (!!data.value?.otherTests[key]?.content &&
                                data.value?.otherTests[key]?.content.length >
                                    0) ||
                            (!!data.value?.otherTests[key]?.images &&
                                data.value?.otherTests[key]?.images.length > 0)
                        ) {
                            if (otherTestsNeedDate.includes(key)) {
                                if (!data.value?.otherTests[key]?.date) {
                                    showNotification(
                                        severityNotification.error,
                                        `${translateCommons(
                                            'Please enter your examination time for ',
                                        )} ${translateKeys(
                                            otherTestsTypeOptions.find(
                                                item => item?.value === key,
                                            ).label,
                                        )}`,
                                    )

                                    otherTestsErrors.push(key + 'Date')
                                    return
                                }
                            }
                        } else {
                            otherTestsErrors.push(key)
                        }
                    })
                } else {
                    otherTestsErrors.push(...data.value?.otherTestsType)
                }
            } else {
                isOtherTestsEmpty = true
            }

            if (otherTestsErrors?.length > 0) {
                errors.value = {
                    otherTestsErrors: [...otherTestsErrors],
                }

                showNotification(
                    severityNotification.error,
                    `${translateCommons(
                        'Please enter your examination information for other tests',
                    )}`,
                )

                return
            }

            if (
                Object.keys(data.value).filter(key => key !== 'gender')
                    ?.length < 1 &&
                isBloodTestsEmpty &&
                isOtherTestsEmpty &&
                isRadiologyEmpty
            ) {
                showNotification(
                    severityNotification.error,
                    'Examination information must have at least one information field !!',
                )
                return
            }

            let toPostData = {
                ...data.value,
                userId: id,
            }

            const examinationData = await createOrUpdateExamination(toPostData)

            if (examinationData?.id) {
                const {examinationKeys} = QueryKeyManager(
                    QueryKeyTypes.examination,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries({
                    queryKey: examinationKeys.pattern(),
                })

                // showNotification(
                //     severityNotification.success,
                //     data.value?.id ? 'Update successfully' : 'Create successfully',
                // )

                // try {
                //     reloadDataTable(QueryTableKey + id, examinationData)
                // } catch (err) {
                //     console.log('Fail to reload table')
                // }

                handleHideForm()
            }
        } else {
            showNotification(
                severityNotification.error,
                'Examination information must have at least one information field !!',
            )
            return
        }
    }

    const handleOnChange = useCallback(
        (parentKey, key, value, type) => {
            let holderData = {}
            let childData = {}

            if (!parentKey || !key) {
                return
            }

            if (parentKey === 'generalTestingInformation') {
                if (!!data.value && !!data.value[key]) {
                    childData = {
                        ...data.value[key],
                        [type]: value,
                        // type === 'images'
                        //     ? !!data.value[key][type]
                        //         ? [...data.value[key][type], ...value]
                        //         : [...value]
                        //     : value,
                    }
                } else {
                    childData = {[type]: value}
                }

                holderData = {
                    ...data.value,
                    [key]: childData,
                }
            } else {
                if (!!data.value[parentKey] && !!data.value[parentKey][key]) {
                    childData = {
                        ...data.value[parentKey][key],
                        [type]: value,
                        // type === 'images'
                        //     ? !!data.value[parentKey][key][type]
                        //         ? [
                        //               ...data.value[parentKey][key][type],
                        //               ...value,
                        //           ]
                        //         : [...value]
                        //     : value,
                    }
                } else {
                    childData = {
                        [type]: value,
                    }
                }

                holderData = {
                    ...data.value,
                    [parentKey]: {
                        ...data.value[parentKey],
                        [key]: childData,
                    },
                }
            }

            data.value = {
                ...holderData,
            }
        },
        [data],
    )

    const handleOnChangeTypes = useCallback(
        (key, value) => {
            if (!!key) {
                data.value = {
                    ...data.value,
                    [key]: value ? [...value] : [],
                }
            }
        },
        [data],
    )

    const getOptionTypeByKey = (type, key) => {
        let testType = {}

        if (!type || !key) {
            return
        }

        if (type === examinationType.bloodsTests) {
            testType = bloodsTestsTypeOptions.find(
                itemOps => itemOps.value === key,
            )
        } else if (type === examinationType.radiology) {
            testType = radiologyOptions.find(itemOps => itemOps.value === key)
        } else if (type === examinationType.otherTests) {
            testType = otherTestsTypeOptions.find(
                itemOps => itemOps.value === key,
            )
        } else return {key: '', label: ''}

        return {key: testType.value, label: testType.label}
    }

    //#endregion

    //#region Render
    const getTimeDataWithTz = (dateTime, pattern = 'DD-MM-YYYY HH:mm') => {
        const dataTime = dateTime ? moment(dateTime) : moment()

        const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone

        const dateWithClientTz = moment.tz(dataTime.format(pattern), timezoneId)
        const convertedUTCDate = dateWithClientTz.utc()
        const dateWithUserTz = convertedUTCDate.clone().tz(userTz)

        return {
            clientTz: `${timezoneId} GMT ${dataTime
                .tz(timezoneId)
                .format('Z')}`,
            clientTime: dataTime.format(`${pattern}`),
            userTz: `${userTz} GMT ${dataTime.tz(userTz).format('Z')}`,
            userTime: dataTime.format(pattern),
        }
    }

    const renderCalendarFooter = useCallback(
        (parentKey, key) => {
            if (
                !data.value[parentKey] ||
                !data.value[parentKey][key] ||
                !data.value[parentKey][key]?.date
            ) {
                return null
            }
            const pattern = 'DD-MM-YYYY HH:mm A'
            let dateValue = data.value[parentKey][key]?.date

            const dataTime = getTimeDataWithTz(dateValue, pattern)

            return (
                <div className="col-12">
                    <div className="font-semibold flex justify-content-center">
                        <p className="mr-2">
                            {translateCommons(
                                "Date time based on Client's time zone ",
                            )}{' '}
                            ({dataTime.clientTz})
                        </p>
                        <p className="text-primary">{dataTime.clientTime}</p>
                    </div>
                    <div className="font-semibold flex justify-content-center">
                        <p className="mr-2">
                            {translateCommons('Your current date time ')} (
                            {dataTime.userTz}
                            ):
                        </p>
                        <p className="text-primary">{dataTime.userTime}</p>
                    </div>
                </div>
            )
        },
        [data.value, getTimeDataWithTz, translateCommons],
    )

    const renderDateInput = useCallback(
        (parentKey, item) => {
            if (!item || !item?.key || !item?.label) {
                return
            }

            const childItem =
                !!data.value[parentKey] && !!data.value[parentKey][item?.key]
                    ? data.value[parentKey][item?.key]
                    : {}

            return (
                <div
                    id={item.key}
                    className={`col-12 px-0 border-bottom-1 border-200 pb-3`}
                    key={`date-${item.key}`}>
                    <h5>{translateKeys(item?.label + ' Date')}</h5>
                    <div className="col-12 p-0">
                        <div className="col-12 p-0">
                            <Calendar
                                locale={
                                    getLanguageCode() === 'gb'
                                        ? 'en'
                                        : getLanguageCode()
                                }
                                className={`border-round col-12 m-0 p-0 overlay`}
                                value={
                                    !!childItem && !!childItem?.date
                                        ? new Date(childItem?.date)
                                        : null
                                }
                                onChange={e =>
                                    handleOnChange(
                                        parentKey,
                                        item?.key,
                                        !!e?.value ? e.value : new Date(),
                                        'date',
                                    )
                                }
                                showTime={true}
                                placeholder={translateKeys(
                                    `${translateCommons("Select patient's")} ${
                                        translateKeys(item?.label) +
                                        ' ' +
                                        translateCommons('Date')
                                    } ${translateCommons(
                                        'here (Required if',
                                    )} ${translateKeys(
                                        item?.label,
                                    )} ${translateCommons('is filled')})`,
                                )}
                                disabled={item.disabled}
                                hourFormat="24"
                                defaultValue={
                                    item.initialValue
                                        ? item.initialValue
                                        : item.defaultValue
                                }
                                footerTemplate={() =>
                                    renderCalendarFooter(parentKey, item?.key)
                                }
                            />
                        </div>
                    </div>
                    {parentKey === 'otherTests' ? (
                        !!errors.value &&
                        errors.value?.otherTestsErrors &&
                        errors.value?.otherTestsErrors?.length > 0 &&
                        errors.value?.otherTestsErrors.includes(
                            item?.key + 'Date',
                        ) ? (
                            <small className="p-error flex align-items-center">
                                <i className="pi pi-exclamation-circle mr-2" />
                                {translateKeys(
                                    otherTestsTypeOptions.find(
                                        itemOps => itemOps.value === item?.key,
                                    )?.label,
                                )}
                                {translateCommons('Date is required !!')}
                            </small>
                        ) : (
                            ''
                        )
                    ) : parentKey === 'radiology' ? (
                        !!errors.value &&
                        errors.value?.radiologyErrors &&
                        errors.value?.radiologyErrors?.length > 0 &&
                        errors.value?.radiologyErrors.includes(
                            item?.key + 'Date',
                        ) ? (
                            item?.key === 'mammogram' ? (
                                <small className="p-error flex align-items-center">
                                    <i className="pi pi-exclamation-circle mr-2" />
                                    {translateCommons(
                                        'Mammogram for Female Date is required !!',
                                    )}
                                </small>
                            ) : (
                                <small className="p-error flex align-items-center">
                                    <i className="pi pi-exclamation-circle mr-2" />
                                    {translateKeys(
                                        radiologyOptions.find(
                                            itemOps =>
                                                itemOps.value === item?.key,
                                        )?.label,
                                    )}{' '}
                                    {translateCommons('Date is required !!')}
                                </small>
                            )
                        ) : (
                            ''
                        )
                    ) : (
                        ''
                    )}
                </div>
            )
        },
        [
            data,
            errors,
            handleOnChange,
            renderCalendarFooter,
            translateCommons,
            translateKeys,
        ],
    )

    const renderUploadButton = (parentKey, childKey, label, childData) => {
        return (
            <>
                <div
                    className={`absolute bottom-0 right-0 m-2 cursor-pointer`}
                    onClick={() =>
                        imageUploadRef.current.showForm({
                            parentKey,
                            childKey,
                            label,
                            images: childData?.images || [],
                        })
                    }>
                    <i
                        data-pr-tooltip={translateCommons(
                            "Preview Examination's Image",
                        )}
                        className={`tooltip-${childKey} bg-primary hover:bg-teal-700 p-2 border-round pi pi-upload p-text-secondary p-overlay-badge`}
                        style={{fontSize: '1.5rem'}}>
                        <Badge
                            value={
                                !!childData?.images
                                    ? childData?.images?.length
                                    : 0
                            }
                            severity="warning"
                            className="absolute top-0 right-0"></Badge>
                    </i>
                    <Tooltip
                        target={`.tooltip-${childKey}`}
                        position="bottom"
                    />
                </div>
            </>
        )
    }

    const renderOptionsInputItem = useCallback(
        (parentKey, item) => {
            if (!parentKey || !item?.key) {
                return
            }

            let values = !!data.value[item.key] ? [...data.value[item.key]] : []

            if (!values || values?.length < 1) {
                return
            }
            return (
                <motion.div>
                    {values.map(childKey => {
                        const childItemOption = item?.options?.find(
                            itemOps => itemOps.value === childKey,
                        )
                        const childItem =
                            !!data.value[parentKey] &&
                            !!data.value[parentKey][childKey]
                                ? data.value[parentKey][childKey]
                                : {}

                        const isErrors =
                            errors.value[`${parentKey}Errors`] &&
                            errors.value[`${parentKey}Errors`]?.length > 0 &&
                            errors.value[`${parentKey}Errors`].includes(
                                childKey,
                            )
                        return (
                            <div
                                id={childKey}
                                className={`col-12`}
                                key={`childItem-${childKey}`}>
                                <h5>{translateKeys(childItemOption?.label)}</h5>
                                <div>
                                    <div className="relative">
                                        <div className="relative">
                                            <InputTextarea
                                                className={`col-12`}
                                                onChange={e =>
                                                    handleOnChange(
                                                        parentKey,
                                                        childKey,
                                                        e.target?.value,
                                                        'content',
                                                    )
                                                }
                                                value={
                                                    childItem?.content
                                                        ? childItem?.content
                                                        : ''
                                                }
                                                placeholder={`${translateCommons(
                                                    "Input patient's",
                                                )} ${translateKeys(
                                                    childItemOption?.label,
                                                )} ${translateCommons('here')}`}
                                                rows={5}
                                                cols={30}
                                                autoResize
                                            />
                                            {/* Button Upload Image */}
                                            {renderUploadButton(
                                                parentKey,
                                                childKey,
                                                translateKeys(
                                                    childItemOption?.label,
                                                ),
                                                childItem,
                                            )}
                                        </div>
                                        {!!isErrors ? (
                                            <small className="p-error flex align-items-center">
                                                <i className="pi pi-exclamation-circle mr-2" />
                                                {translateKeys(
                                                    childItemOption?.label,
                                                )}{' '}
                                                {translateCommons(
                                                    'is required !!',
                                                )}
                                            </small>
                                        ) : (
                                            ''
                                        )}
                                        {!!inputsNeedDate.includes(childKey) &&
                                            renderDateInput(parentKey, {
                                                key: childItemOption?.value,
                                                label: translateKeys(
                                                    childItemOption?.label,
                                                ),
                                            })}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </motion.div>
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            data,
            errors,
            handleOnChange,
            renderDateInput,
            translateCommons,
            translateKeys,
        ],
    )

    const renderItem = useCallback(
        (parentKey, itemList) => {
            return itemList?.map((item, idx) => {
                let childItemData = {}

                if (data.value) {
                    if (parentKey === 'generalTestingInformation') {
                        if (data.value[item?.key]) {
                            childItemData = data.value[item?.key]
                        }
                    } else {
                        if (
                            !!data.value[parentKey] &&
                            !!data.value[parentKey][item?.key]
                        ) {
                            childItemData = data.value[parentKey][item?.key]
                        }
                    }
                }

                return (
                    <div
                        id={item.key}
                        className={`${
                            item.className ? item.className : 'col-12'
                        } `}
                        key={`item-${item.key}`}>
                        <h5>
                            {translateKeys(item?.label)}
                            {item.shorten && <span> ( {item.shorten} )</span>}
                        </h5>
                        {/* RENDER COMPONENTS */}
                        {/* Multiselect Component use for Select Test Type */}
                        {item.component === component.multiSelect ? (
                            <div className="col-12 p-0">
                                <span className={`col-12 px-0 `}>
                                    <MultiSelect
                                        className={`col-12 p-inputtext p-0 `}
                                        value={data.value[item?.key] || ''}
                                        defaultValue={
                                            item.initialValue
                                                ? item.initialValue
                                                : item.defaultValue
                                        }
                                        options={translateOptions(item.options)}
                                        onChange={e =>
                                            handleOnChangeTypes(
                                                item?.key,
                                                e?.value,
                                            )
                                        }
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder={translateKeys(
                                            item.placeholder,
                                        )}
                                        maxSelectedLabels={3}
                                        emptyFilterMessage={translateCommons(
                                            'No results found',
                                        )}
                                        resetFilterOnHide
                                        display="chip"
                                        filter
                                        filterBy="label"
                                        filterPlaceholder={translateCommons(
                                            'Search here',
                                        )}
                                        style={{
                                            '--inputColor':
                                                'rgba(68, 184, 166, 0.1)',
                                        }}
                                    />
                                </span>
                                {renderOptionsInputItem(parentKey, item)}
                            </div>
                        ) : item.component === component.inputTextArea ? (
                            <motion.div className="relative">
                                <div className="relative">
                                    <InputTextarea
                                        id={item.key}
                                        className={`col-12`}
                                        onChange={e =>
                                            handleOnChange(
                                                parentKey,
                                                item?.key,
                                                e?.target?.value,
                                                'content',
                                            )
                                        }
                                        value={childItemData?.content || ''}
                                        placeholder={translateKeys(
                                            item.placeholder,
                                        )}
                                        rows={5}
                                        cols={30}
                                        autoResize
                                    />

                                    {/* Button Upload Image */}
                                    {renderUploadButton(
                                        parentKey,
                                        item?.key,
                                        item?.label
                                            ? translateKeys(item?.label)
                                            : translateKeys(
                                                  convertKeyToLabel(item?.key),
                                              ),
                                        childItemData,
                                    )}
                                </div>

                                {!!inputsNeedDate.includes(item?.key) &&
                                    renderDateInput(parentKey, item)}
                            </motion.div>
                        ) : null}
                        {item?.emptyMessage && (
                            <div className="col-12 px-0" style={{color: 'red'}}>
                                {item.emptyMessage}
                            </div>
                        )}
                    </div>
                )
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            data,
            handleOnChange,
            handleOnChangeTypes,
            renderDateInput,
            renderOptionsInputItem,
            translateCommons,
            translateKeys,
            translateOptions,
        ],
    )

    const menuList = useMemo(() => {
        let menuListHolder = []
        const typeList = ['bloodsTestsType', 'radiologyType', 'otherTestsType']

        if (!dataList || dataList?.length < 0) {
            return menuListHolder
        }

        for (let i = 0; i < dataList.length; i++) {
            let itemList = {
                key: dataList[i].key,
                label: convertKeyToLabel(dataList[i].key),
            }
            let childItem = []
            if (dataList[i].children?.length > 0) {
                for (let j = 0; j < dataList[i].children.length; j++) {
                    childItem.push({
                        key: dataList[i].children[j]?.key,
                        label: dataList[i].children[j]?.label,
                    })

                    if (
                        typeList.includes(dataList[i].children[j]?.key) &&
                        dataList[i].children[j]?.initialValue?.length > 0
                    ) {
                        let test = [
                            ...dataList[i].children[j]?.initialValue.map(
                                itemKey => {
                                    const test = getOptionTypeByKey(
                                        dataList[i]?.key,
                                        itemKey,
                                    )
                                    return test
                                },
                            ),
                        ]
                        childItem.push(...test)
                    }
                }

                itemList['children'] = childItem
            }

            menuListHolder.push(itemList)
        }

        return menuListHolder
    }, [dataList])

    const renderMenuList = useCallback(() => {
        return (
            !!menuList &&
            menuList?.length > 0 &&
            menuList?.map(item => (
                <div
                    key={`menu-${item.key}`}
                    className="mt-2 p-2 border-round-lg"
                    style={{background: '#f9f9f9'}}>
                    <h6 className="font-bold m-0 p-1">
                        <a
                            className=" text-700"
                            style={{color: 'rgb(73, 80, 87'}}
                            href={`#${item.key}`}>
                            <i className="pi pi-tags" />{' '}
                            {translateKeys(convertKeyToLabel(item.key))}
                        </a>
                    </h6>
                    <ul className="pl-2 list-none">
                        {item.children.map(itemMenu => (
                            <li key={itemMenu.key} className="m-1">
                                <a
                                    className="flex gap-2 text-900 hover:text-primary"
                                    href={`#${itemMenu.key}`}>
                                    <i
                                        className="pi pi-angle-right"
                                        style={{fontSize: '1rem'}}
                                    />{' '}
                                    {translateKeys(itemMenu.label)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            ))
        )
    }, [menuList, translateKeys])

    const renderFlexItem = useCallback(() => {
        return dataList?.map(item => (
            <div
                id={item.key}
                key={`flex-${item.key}`}
                className="flex flex-column col-12 p-0"
                style={{borderBottom: '1px dashed lightgray'}}>
                <div className="col-12" style={{position: 'relative'}}>
                    <div className="sticky">
                        <div className="mb-2">
                            <h4 className="mb-1 font-bold text-darkcyan">
                                {translateKeys(convertKeyToLabel(item.key))}
                            </h4>
                        </div>
                        {item?.description &&
                        typeof item?.description === 'string' ? (
                            <p>{translateCommons(item.description)}</p>
                        ) : (
                            <div>{item.description}</div>
                        )}
                    </div>
                </div>
                {item.children && (
                    <div className="col-12">
                        {renderItem(item.key, item.children)}
                    </div>
                )}
            </div>
        ))
    }, [dataList, renderItem, translateCommons, translateKeys])

    //#endregion

    return (
        <>
            <Sidebar
                className="p-sidebar-full overlay-behind-calendar"
                visible={visible.value}
                onHide={handleHideForm}
                style={{
                    scrollBehavior: 'smooth',
                    zIndex: '3000 !important',
                }}
                blockScroll
                fullScreen>
                {!!visible.value && (
                    <PageContentWrapper>
                        <FormHeader
                            header={translateDatatable('EXAMINATION FORM')}
                            onHide={handleHideForm}
                        />
                        <div className="grid w-full">
                            <div className="col-12 lg:col-10">
                                {renderFlexItem()}
                            </div>

                            {/* Render Table Of Content */}
                            <div className="hidden lg:block lg:col-2 relative">
                                <div className="sticky col-12 overflow-y-scroll h-screen">
                                    <p className="font-bold text-darkcyan text-center text-lg">
                                        {translateCommons(
                                            'Table of contents for examinations',
                                        )}
                                    </p>
                                    {renderMenuList()}
                                </div>
                                {/* <Button
                                className="absolute bottom-0 right-0 m-2 lg:hidden block"
                                icon="pi pi-list"
                                tooltip={translateDatatable(
                                    'Table of contents for examinations',
                                )}
                                onClick={() => {}}
                                /> */}
                            </div>
                        </div>

                        <div className="flex col-12 py-3">
                            <Button
                                className={`col-12 p-button-lg font-bold mr-2`}
                                icon={'pi pi-send'}
                                label={translateCommons('SUBMIT')}
                                onClick={handleOnSubmit}
                            />
                        </div>
                    </PageContentWrapper>
                )}
            </Sidebar>

            <ExaminationUploaderForm
                ref={imageUploadRef}
                callBack={data => {
                    if (
                        !!data &&
                        !!data?.key &&
                        !!data?.type &&
                        !!data?.value &&
                        !!data?.parentKey
                    ) {
                        handleOnChange(
                            data?.parentKey,
                            data?.key,
                            data?.value,
                            data?.type,
                        )
                    }
                }}
            />
            {console.log(data.value)}
        </>
    )
})

export default ExaminationForm
